/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import {
  stripeConnectStatusEnum,
  useRequestCommunitiesStripeConnectDashboard,
  useRequestCommunitiesStripeConnectStatus,
  useRequestMutationsCommunitiesStripeConnect,
} from "@packages/sdk";
import type { ThemeType } from "@packages/ui";
import {
  Button,
  CheckmarkIcon,
  NoticeIcon,
  spacing,
  useErrorNotification,
} from "@packages/ui";
import { useState } from "react";

type GivingStripeConnectButtonProps = {
  communityId: number;
  className?: string;
  isFullWidth?: boolean;
  size?: "s" | "m" | "l";
};

const successTextSyles =
  (): Interpolation<ThemeType> => (theme: ThemeType) => ({
    color: theme.semanticColors.successSuccess,
    alignItems: "center",
    display: "flex",
    flexShrink: 0,
    gap: spacing.s,
    whiteSpace: "nowrap",
  });

/**
 * Giving tab CTA whose label and behavior are driven entirely by the
 * server-reported Stripe Connect status (never by client-side "did the user
 * click" state). Uses a single fetch on mount — polling only happens on the
 * Stripe return/refresh landing pages.
 */
export const GivingStripeConnectButton = ({
  communityId,
  ...props
}: GivingStripeConnectButtonProps) => {
  const t = useTranslations();
  const { track } = useAnalytics();
  const size = props.size || "l";

  const { query: statusQuery } = useRequestCommunitiesStripeConnectStatus({
    id: communityId,
  });
  const { query: dashboardQuery } = useRequestCommunitiesStripeConnectDashboard(
    {
      enabled: false,
      id: communityId,
    },
  );
  const { mutationConnect } = useRequestMutationsCommunitiesStripeConnect({
    id: communityId,
  });
  const [isRedirecting, setIsRedirecting] = useState(false);

  useErrorNotification({
    isError:
      statusQuery.isError || mutationConnect.isError || dashboardQuery.isError,
  });

  if (statusQuery.isLoading) return null;

  if (statusQuery.isError) {
    return (
      <Button variant="primary" size={size} disabled {...props}>
        {t("giving_stripe_connect_set_up_account")}
      </Button>
    );
  }

  const status = statusQuery.data?.status;

  const handleDashboard = async () => {
    track({ event: HallowAnalyticsEvent.TappedViewStripeDashboard });
    setIsRedirecting(true);
    const { data } = await dashboardQuery.refetch();
    window.open(data.url, "_blank");
  };

  if (status === stripeConnectStatusEnum.enum.connected) {
    return (
      <Button
        variant="neutral"
        css={successTextSyles()}
        size={size}
        onClick={handleDashboard}
        isLoading={dashboardQuery.isFetching || isRedirecting}
        startIcon={<CheckmarkIcon.FilledOn />}
        {...props}
      >
        {t("giving_stripe_connect_account_connected")}
      </Button>
    );
  }

  const handleConnect = async () => {
    try {
      const { url } = await mutationConnect.mutateAsync();
      setIsRedirecting(true);
      window.location.href = url;
    } catch {
      // Surfaced to the user via useErrorNotification above.
    }
  };

  if (status === stripeConnectStatusEnum.enum.restricted) {
    return (
      <Button
        variant="neutral"
        size={size}
        onClick={async () => {
          track({
            event: HallowAnalyticsEvent.TappedActionNeededStripeAccount,
          });
          await handleConnect();
        }}
        isLoading={mutationConnect.isPending || isRedirecting}
        startIcon={<NoticeIcon.Circled />}
        {...props}
      >
        {t("giving_stripe_connect_action_needed")}
      </Button>
    );
  }

  return (
    <Button
      variant="primary"
      size={size}
      onClick={async () => {
        track({ event: HallowAnalyticsEvent.TappedSetUpStripeAccount });
        await handleConnect();
      }}
      isLoading={mutationConnect.isPending || isRedirecting}
      {...props}
    >
      {t("giving_stripe_connect_set_up_account")}
    </Button>
  );
};
